// src/components/exercise/ExerciseSession.tsx
import React, { useEffect, useState } from 'react';
import { useExerciseStore } from '../../stores/useExerciseStore';
import { useThemePalette }  from '../../hooks/useThemePalette';
import { useThemeStore }    from '../../stores/useThemeStore';
import ContentViewer   from './ContentViewer';
import SectionOverview from './SectionOverview';
import ExercisePanel   from './ExercisePanel';
import CompletionScreen from './CompletionScreen';

const MOBILE_BP = 768;

const ExerciseSession: React.FC = () => {
  const C      = useThemePalette();
  const isDark = useThemeStore((s) => s.theme) === 'dark';

  const currentSectionId = useExerciseStore(s => s.currentSectionId);
  const isCompletion     = useExerciseStore(s => s.isCompletion);
  const currentUnitId    = useExerciseStore(s => s.currentUnitId);
  const exerciseData     = useExerciseStore(s => s.exerciseData);
  const closeSession     = useExerciseStore(s => s.closeSession);

  const [isMobile,   setIsMobile]   = useState(() => (typeof window !== 'undefined' ? window.innerWidth : 1024) < MOBILE_BP);
  const [activeTab,  setActiveTab]  = useState<'content' | 'exercises'>('exercises');
  const [closeHover, setCloseHover] = useState(false);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < MOBILE_BP);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => { if (currentSectionId !== null) setActiveTab('exercises'); }, [currentSectionId]);
  useEffect(() => { if (isCompletion) setActiveTab('exercises'); }, [isCompletion]);

  const unit      = currentUnitId ? exerciseData[currentUnitId] : null;
  const unitTitle = (unit as any)?.title as string | undefined ?? 'Unit';

  let rightPanel: React.ReactNode;
  if (isCompletion)               rightPanel = <CompletionScreen />;
  else if (currentSectionId !== null) rightPanel = <ExercisePanel />;
  else                            rightPanel = <SectionOverview />;

  const panelTag = isCompletion ? 'Results' : currentSectionId !== null ? 'Exercise' : 'Overview';

  // Glassmorphism header background — adapts to theme
  const headerBg = isDark ? 'rgba(13,18,32,0.95)' : 'rgba(255,255,255,0.95)';

  const CloseButton = (
    <button
      onClick={() => void closeSession()}
      onMouseEnter={() => setCloseHover(true)}
      onMouseLeave={() => setCloseHover(false)}
      style={{
        padding:      '9px 18px',
        borderRadius: '999px',
        border:       `1px solid ${closeHover ? 'rgba(248,113,113,0.4)' : C.border}`,
        background:   closeHover ? 'rgba(248,113,113,0.1)' : isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)',
        color:        closeHover ? C.error : C.muted,
        fontSize:     '0.72rem',
        fontWeight:   700,
        letterSpacing:'0.08em',
        textTransform:'uppercase',
        cursor:       'pointer',
        transition:   'border-color 0.12s ease, background 0.12s ease, color 0.12s ease',
        flexShrink:   0,
        display:      'flex',
        alignItems:   'center',
        gap:          '6px',
        fontFamily:   'inherit',
      }}
    >
      <svg width="10" height="10" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <line x1="1" y1="1" x2="13" y2="13" /><line x1="13" y1="1" x2="1" y2="13" />
      </svg>
      Close
    </button>
  );

  // ── Mobile ────────────────────────────────────────────────────────────────
  if (isMobile) {
    return (
      <div style={{ position: 'fixed', inset: 0, background: C.surface, display: 'flex', flexDirection: 'column', zIndex: 100 }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 16px',
          borderBottom: `1px solid ${C.border}`,
          background: headerBg,
          backdropFilter: 'blur(20px)',
          flexShrink: 0,
        }}>
          <div style={{
            fontSize: '0.9rem', fontWeight: 700, color: C.text,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            flex: 1, marginRight: '12px',
          }}>
            {unitTitle}
          </div>
          {CloseButton}
        </div>

        <div style={{
          display: 'flex',
          background: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
          borderBottom: `1px solid ${C.border}`,
          flexShrink: 0,
        }}>
          {(['content', 'exercises'] as const).map(tab => {
            const active = activeTab === tab;
            return (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                style={{
                  flex:         1,
                  padding:      '12px 0',
                  border:       'none',
                  background:   'transparent',
                  color:        active ? C.accent : C.muted,
                  fontWeight:   700,
                  fontSize:     '0.72rem',
                  letterSpacing:'0.08em',
                  textTransform:'uppercase',
                  cursor:       'pointer',
                  borderBottom: `2px solid ${active ? C.accent : 'transparent'}`,
                  transition:   'all 0.2s ease',
                  fontFamily:   'inherit',
                }}
              >
                {tab === 'content' ? 'Content' : 'Exercises'}
              </button>
            );
          })}
        </div>

        <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {activeTab === 'content' ? <ContentViewer /> : rightPanel}
        </div>
      </div>
    );
  }

  // ── Desktop ───────────────────────────────────────────────────────────────
  return (
    <div style={{ position: 'fixed', inset: 0, background: C.surface, display: 'flex', flexDirection: 'column', zIndex: 100 }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 28px',
        borderBottom: `1px solid ${C.border}`,
        background: headerBg,
        backdropFilter: 'blur(20px)',
        flexShrink: 0,
        minHeight: '56px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: C.accent, boxShadow: `0 0 8px ${C.accent}`, flexShrink: 0 }} />
          <span style={{ fontSize: '0.9rem', fontWeight: 700, color: C.text }}>{unitTitle}</span>
          <span style={{
            fontSize: '0.62rem', fontWeight: 700, color: C.muted,
            background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)',
            border: `1px solid ${C.border}`,
            borderRadius: '6px', padding: '2px 8px',
            letterSpacing: '0.08em', textTransform: 'uppercase',
          }}>
            {panelTag}
          </span>
        </div>
        {CloseButton}
      </div>

      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        <div style={{ width: '50%', borderRight: `1px solid ${C.border}`, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <ContentViewer />
        </div>
        <div style={{ width: '50%', display: 'flex', flexDirection: 'column', overflow: 'hidden', background: C.surface }}>
          {rightPanel}
        </div>
      </div>
    </div>
  );
};

export default ExerciseSession;